import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";

const CATEGORIES = [
  "Professional Sports Team",
  "College Athletics",
  "Sports Equipment",
  "Apparel",
  "Technology",
  "Hospitality",
  "Casino",
  "Resort",
  "Retail Development",
  "Corporate Sponsor",
  "Strategic Investor",
  "Franchise Development",
  "Media",
  "Other",
] as const;

const INTERESTS = [
  "Sponsorship",
  "Licensing",
  "Venue Partnership",
  "Technology",
  "Investment",
  "Franchise",
  "Marketing",
  "Other",
] as const;

const submitSchema = z.object({
  company: z.string().trim().min(1).max(200),
  contact_name: z.string().trim().min(2).max(120),
  title: z.string().trim().max(120).optional().nullable(),
  email: z.string().trim().email().max(200),
  phone: z.string().trim().max(40).optional().nullable(),
  website_url: z.string().trim().max(300).optional().nullable(),
  category: z.enum(CATEGORIES),
  organization_size: z.string().trim().max(80).optional().nullable(),
  message: z.string().max(2000).optional().nullable(),
  budget_range: z.string().trim().max(80).optional().nullable(),
  timeline: z.string().trim().max(120).optional().nullable(),
  interests: z.array(z.enum(INTERESTS)).default([]),
  website: z.string().optional().nullable(), // honeypot
  source: z.string().max(80).optional().nullable(),
});

async function sha256(input: string): Promise<string> {
  const enc = new TextEncoder().encode(input);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function generateToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  let bin = "";
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

export const submitPartnershipRequest = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => submitSchema.parse(data))
  .handler(async ({ data }) => {
    if (data.website && data.website.trim().length > 0) {
      return { ok: true } as const;
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const tenMinAgo = new Date(Date.now() - 10 * 60 * 1000).toISOString();
    const { count: recentCount } = await supabaseAdmin
      .from("partnership_requests")
      .select("id", { count: "exact", head: true })
      .eq("email", data.email.toLowerCase())
      .gte("created_at", tenMinAgo);
    if ((recentCount ?? 0) >= 3) {
      return { ok: false, error: "Too many requests. Please try again later." } as const;
    }

    const request = getRequest();
    const ip =
      request?.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      request?.headers.get("cf-connecting-ip") ||
      "";
    const ipHash = ip ? await sha256(ip) : null;

    const token = generateToken();

    const { data: inserted, error } = await supabaseAdmin
      .from("partnership_requests")
      .insert({
        company: data.company,
        contact_name: data.contact_name,
        title: data.title ?? null,
        email: data.email.toLowerCase(),
        phone: data.phone ?? null,
        website: data.website_url ?? null,
        category: data.category,
        organization_size: data.organization_size ?? null,
        message: data.message ?? null,
        budget_range: data.budget_range ?? null,
        timeline: data.timeline ?? null,
        interests: data.interests,
        package_access_token: token,
        ip_hash: ipHash,
        source: data.source || "website",
      })
      .select("id, package_access_token")
      .single();

    if (error || !inserted) {
      console.error("[partnership] insert failed", error);
      return { ok: false, error: "Could not save request. Please try again." } as const;
    }

    const origin =
      request?.headers.get("origin") ||
      (request ? new URL(request.url).origin : "https://clutchcube.com");
    const packageUrl = `${origin}/partnership-package/${inserted.package_access_token}`;

    const { sendEmail } = await import("./emails/send");
    const { partnershipWelcomeEmail, partnershipAdminNotificationEmail } = await import(
      "./emails/templates"
    );

    const partnerTpl = partnershipWelcomeEmail({
      contactName: data.contact_name,
      company: data.company,
      packageUrl,
    });
    const partnerRes = await sendEmail({
      to: data.email,
      subject: partnerTpl.subject,
      html: partnerTpl.html,
    });

    const adminEmail = process.env.ADMIN_EMAIL;
    let adminRes: { ok: boolean; id?: string; error?: string } = {
      ok: false,
      error: "ADMIN_EMAIL not set",
    };
    if (adminEmail) {
      const adminTpl = partnershipAdminNotificationEmail({
        company: data.company,
        contactName: data.contact_name,
        title: data.title,
        email: data.email,
        phone: data.phone,
        website: data.website_url,
        category: data.category,
        organizationSize: data.organization_size,
        budgetRange: data.budget_range,
        timeline: data.timeline,
        interests: data.interests,
        message: data.message,
        source: data.source || "website",
        leadId: inserted.id,
      });
      adminRes = await sendEmail({
        to: adminEmail,
        subject: adminTpl.subject,
        html: adminTpl.html,
        reply_to: data.email,
      });
    } else {
      console.warn("[partnership] ADMIN_EMAIL not configured");
    }

    const now = new Date().toISOString();
    await supabaseAdmin
      .from("partnership_requests")
      .update({
        email_sent: partnerRes.ok,
        email_sent_at: partnerRes.ok ? now : null,
        admin_notified: adminRes.ok,
        admin_notified_at: adminRes.ok ? now : null,
        status: partnerRes.ok ? "emailed" : "new",
      })
      .eq("id", inserted.id);

    return {
      ok: true,
      emailSent: partnerRes.ok,
      adminNotified: adminRes.ok,
    } as const;
  });

export const getPartnershipPackageByToken = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) =>
    z.object({ token: z.string().min(10).max(200) }).parse(data),
  )
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: row, error } = await supabaseAdmin
      .from("partnership_requests")
      .select(
        "id, contact_name, company, category, package_view_count, first_package_viewed_at, status, lead_score",
      )
      .eq("package_access_token", data.token)
      .maybeSingle();

    if (error || !row) {
      return { ok: false as const, error: "invalid_token" as const };
    }

    const now = new Date().toISOString();
    const isFirstView = !row.first_package_viewed_at;
    const scoreDelta = isFirstView ? 15 : 5;
    const nextStatus =
      row.status === "new" || row.status === "emailed" ? "viewed_package" : row.status;

    await supabaseAdmin
      .from("partnership_requests")
      .update({
        package_view_count: (row.package_view_count ?? 0) + 1,
        first_package_viewed_at: row.first_package_viewed_at || now,
        last_package_viewed_at: now,
        lead_score: (row.lead_score ?? 10) + scoreDelta,
        status: nextStatus,
      })
      .eq("id", row.id);

    return {
      ok: true as const,
      contactName: row.contact_name,
      company: row.company,
      category: row.category,
    };
  });
